import { getApiClient } from "@src/api";
import { useAuthenticatedUser } from "@src/components/user/user-context";
import { graphql } from "@src/gql";
import {
	type IconType,
	NavBarItemType,
	NavbarSection,
	WhiteboardPermission,
	WhiteboardStatus,
} from "@src/gql/graphql";
import {
	type QueryClient,
	queryOptions,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useMemo, useSyncExternalStore } from "react";

const fetchNavbarBranchQuery = graphql(/* GraphQL */ `
	query fetchNavbarBranch(
		$section: NavbarSection!
		$parentKey: String
		$status: WhiteboardStatus
	) {
		navbar {
			branch(
				section: $section
				parentKey: $parentKey
				where: { status: { eq: $status } }
			) {
				key
				name
				type
				parentKey
				rank
				hasChildren
				status
				icon {
					type
					value
				}
				collaboration {
					isShared
					collaborators {
          				userId
          				permission
          			}
				}
				publication {
					isPublished
				}
			}
		}
	}
`);

// TODO(graphql): same SharedWithMe access field on navbar.tree when available.
const fetchNavbarTreeQuery = graphql(/* GraphQL */ `
	query fetchNavbarTree(
		$section: NavbarSection!
		$status: WhiteboardStatus
	) {
		navbar {
			tree(
				section: $section
				where: { status: { eq: $status } }
			) {
				key
				name
				type
				parentKey
				rank
				hasChildren
				status
				icon {
					type
					value
				}
				collaboration {
					isShared
					collaborators {
          				userId
          				permission
          			}
				}
				publication {
					isPublished
				}
			}
		}
	}
`);

export type NavbarBranchItem = {
	key: string;
	name: string;
	type: NavBarItemType;
	parentKey?: string | null;
	rank: string;
	hasChildren: boolean;
	status?: WhiteboardStatus | null;
	icon?: {
		type: IconType;
		value: string;
	} | null;
	collaboration?: {
		isShared: boolean;
		collaborators?: Array<{
			userId?: string | null;
			permission: WhiteboardPermission;
		}>;
	} | null;
	publication?: { isPublished: boolean } | null;
};

export type SharedWithMeListAccess = Extract<
	WhiteboardPermission,
	WhiteboardPermission.Read | WhiteboardPermission.Write
>;

const SHARED_WITH_ME_LIST_PERMISSIONS = [
	WhiteboardPermission.Read,
	WhiteboardPermission.Write,
] as const satisfies readonly SharedWithMeListAccess[];

function isSharedWithMeListAccess(
	permission: WhiteboardPermission,
): permission is SharedWithMeListAccess {
	return (
		SHARED_WITH_ME_LIST_PERMISSIONS as readonly WhiteboardPermission[]
	).includes(permission);
}

/** Current user's read/write affordance on a Shared With Me board row. */
export function getSharedWithMeListItemAccess(
	item: NavbarBranchItem,
	currentUserId: string,
): SharedWithMeListAccess | undefined {
	const permission = item.collaboration?.collaborators?.find(
		(c) => c.userId === currentUserId,
	)?.permission;
	if (permission == null || !isSharedWithMeListAccess(permission)) {
		return undefined;
	}
	return permission;
}

export function navbarBranchIsSharedOrPublished(
	item: NavbarBranchItem,
): boolean {
	return (
		(item.collaboration?.isShared ?? false) ||
		(item.publication?.isPublished ?? false)
	);
}

/** Root token for navbar per-branch React Query keys (see `navbarBranchQueryKeys`). */
export const NAVBAR_BRANCH_QUERY_KEY_ROOT = "navbar-branch" as const;

export const navbarBranchQueryKeys = {
	all: (): readonly [typeof NAVBAR_BRANCH_QUERY_KEY_ROOT] => [
		NAVBAR_BRANCH_QUERY_KEY_ROOT,
	],
	section: (section: NavbarSection) =>
		[NAVBAR_BRANCH_QUERY_KEY_ROOT, section] as const,
	branch: (section: NavbarSection, parentKey?: string | null) =>
		[NAVBAR_BRANCH_QUERY_KEY_ROOT, section, parentKey ?? "root"] as const,
};

export const fetchNavbarBranchQueryOptions = (
	section: NavbarSection,
	parentKey?: string | null,
	status: WhiteboardStatus = WhiteboardStatus.Active,
) =>
	queryOptions({
		queryKey: navbarBranchQueryKeys.branch(section, parentKey),
		queryFn: async (): Promise<NavbarBranchItem[]> => {
			const client = getApiClient();
			const response = await client.query({
				query: fetchNavbarBranchQuery,
				variables: { section, parentKey, status },
			});
			return (response.data?.navbar?.branch ?? []) as NavbarBranchItem[];
		},
		networkMode: "offlineFirst",
	});

export const navbarTreeQueryKeys = {
	tree: (section: NavbarSection) => ["navbar-tree", section] as const,
};

export const fetchNavbarTreeQueryOptions = (
	section: NavbarSection,
	status: WhiteboardStatus = WhiteboardStatus.Active,
) =>
	queryOptions({
		queryKey: navbarTreeQueryKeys.tree(section),
		queryFn: async (): Promise<NavbarBranchItem[]> => {
			const client = getApiClient();
			const response = await client.query({
				query: fetchNavbarTreeQuery,
				variables: { section, status },
			});
			return (response.data?.navbar?.tree ?? []) as NavbarBranchItem[];
		},
		networkMode: "offlineFirst",
	});

function parentKeyToBranchCacheKey(
	parentKey: string | null | undefined,
): string {
	if (parentKey == null || parentKey === "") return "root";
	return parentKey;
}

/**
 * Seeds per-branch react-query caches from a flat `navbar.tree` result so
 * `useNavBranchItems` reads synchronously without duplicate network requests.
 */
export function seedBranchCacheFromTree(
	queryClient: QueryClient,
	section: NavbarSection,
	items: NavbarBranchItem[],
): void {
	const buckets = new Map<string, NavbarBranchItem[]>();
	for (const item of items) {
		const bk = parentKeyToBranchCacheKey(item.parentKey);
		const list = buckets.get(bk) ?? [];
		list.push(item);
		buckets.set(bk, list);
	}

	const sortByRank = (a: NavbarBranchItem, b: NavbarBranchItem) =>
		a.rank.localeCompare(b.rank, undefined, { numeric: true });

	for (const [bucketKey, branchItems] of buckets) {
		branchItems.sort(sortByRank);
		queryClient.setQueryData<NavbarBranchItem[]>(
			navbarBranchQueryKeys.branch(
				section,
				bucketKey === "root" ? null : bucketKey,
			),
			branchItems,
		);
	}

	const parentKeysWithListedChildren = new Set(buckets.keys());
	for (const item of items) {
		if (item.type !== NavBarItemType.Folder) continue;
		if (!parentKeysWithListedChildren.has(item.key)) {
			queryClient.setQueryData<NavbarBranchItem[]>(
				navbarBranchQueryKeys.branch(section, item.key),
				[],
			);
		}
	}
}

export function useNavbarTreePrefetch(section: NavbarSection) {
	const queryClient = useQueryClient();
	const query = useQuery(fetchNavbarTreeQueryOptions(section));
	useEffect(() => {
		if (query.data) seedBranchCacheFromTree(queryClient, section, query.data);
	}, [queryClient, section, query.data]);
	return query;
}

export interface FlatTreeRow {
	key: string;
	name: string;
	type: NavBarItemType;
	depth: number;
	parentKey: string | null;
	/** My Boards / Favourites: collaborators or published link. */
	isSharedOrPublished: boolean;
	/** Shared With Me boards only; eye vs pencil in UI. */
	sharedWithMeListItemAccess?: SharedWithMeListAccess;
	rank: string;
}

function toFlatRow(
	item: NavbarBranchItem,
	depth: number,
	parentKey: string | null,
	section: NavbarSection,
	currentUserId: string,
): FlatTreeRow {
	const base = {
		key: item.key,
		name: item.name,
		type: item.type,
		depth,
		parentKey,
		rank: item.rank,
	};
	if (
		section === NavbarSection.SharedWithMe &&
		item.type !== NavBarItemType.Folder
	) {
		return {
			...base,
			isSharedOrPublished: false,
			sharedWithMeListItemAccess: getSharedWithMeListItemAccess(
				item,
				currentUserId,
			),
		};
	}
	return {
		...base,
		isSharedOrPublished: navbarBranchIsSharedOrPublished(item),
	};
}

/**
 * Walks the per-branch React Query caches (the same ones the navbar reads,
 * which mutations keep up to date optimistically) and returns a depth-first
 * flat list for modal-style rendering. This is the single source of truth:
 * any optimistic update a mutation performs on the branch caches shows up
 * here immediately.
 */
export function buildFlatTreeFromBranchCache(
	queryClient: QueryClient,
	section: NavbarSection,
	currentUserId: string,
): FlatTreeRow[] {
	const rows: FlatTreeRow[] = [];
	const sortByRank = (a: NavbarBranchItem, b: NavbarBranchItem) =>
		a.rank.localeCompare(b.rank, undefined, { numeric: true });

	const readBranch = (parentKey: string | null): NavbarBranchItem[] => {
		const data = queryClient.getQueryData<NavbarBranchItem[]>(
			navbarBranchQueryKeys.branch(section, parentKey),
		);
		return data ? [...data].sort(sortByRank) : [];
	};

	const traverse = (parentKey: string | null, depth: number) => {
		for (const item of readBranch(parentKey)) {
			rows.push(toFlatRow(item, depth, parentKey, section, currentUserId));
			if (item.type === NavBarItemType.Folder) {
				traverse(item.key, depth + 1);
			}
		}
	};

	traverse(null, 0);
	return rows;
}

/**
 * React hook that returns a depth-first flat list of the whole navbar tree
 * for a section. Subscribes to changes in any per-branch cache (and the
 * tree-prefetch query) so optimistic mutations update the UI immediately.
 */
export function useFlatNavbarTree(section: NavbarSection): FlatTreeRow[] {
	const queryClient = useQueryClient();
	const { id: currentUserId } = useAuthenticatedUser();
	// Ensure the tree endpoint has been fetched at least once and its results
	// seeded into the per-branch caches, so readers have something to read.
	useNavbarTreePrefetch(section);

	// Subscribe to any change under the navbar branch query prefix. This gives
	// us re-renders when mutations update any branch optimistically.
	const branchesSignature = useSyncExternalStore(
		(notify) => {
			let cancelled = false;
			const unsub = queryClient.getQueryCache().subscribe((event) => {
				const key = event.query.queryKey;
				if (
					Array.isArray(key) &&
					key[0] === NAVBAR_BRANCH_QUERY_KEY_ROOT &&
					key[1] === section
				) {
					// Defer so cache updates from another component mounting
					// useQuery (same tick as its render) do not notify here
					// synchronously — that would update MoveToFolderModal etc.
					// while React is still rendering NavTreePanel / TreeBranch.
					queueMicrotask(() => {
						if (!cancelled) notify();
					});
				}
			});
			return () => {
				cancelled = true;
				unsub();
			};
		},
		() => {
			// A cheap signature: concatenated updatedAt stamps of every branch query
			// for this section. Any branch update bumps the string, triggering a
			// re-read through the memo below.
			return queryClient
				.getQueryCache()
				.findAll({ queryKey: navbarBranchQueryKeys.section(section) })
				.map((q) => `${q.queryKey.join("/")}@${q.state.dataUpdatedAt}`)
				.join("|");
		},
		() => "",
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: branchesSignature is the trigger for re-computation when any branch cache entry for this section changes; its value isn't used directly inside the memo body.
	return useMemo(
		() => buildFlatTreeFromBranchCache(queryClient, section, currentUserId),
		[queryClient, section, branchesSignature, currentUserId],
	);
}

/**
 * Returns every navbar branch query key whose cached list contains `itemKey`.
 * Useful for targeted invalidation after mutations that remove/modify a single item.
 */
export function findBranchKeysContaining(
	queryClient: QueryClient,
	itemKey: string,
): readonly (readonly unknown[])[] {
	return queryClient
		.getQueriesData<NavbarBranchItem[]>({
			queryKey: navbarBranchQueryKeys.all(),
		})
		.filter(([, data]) => data?.some((i) => i.key === itemKey))
		.map(([queryKey]) => queryKey);
}

/** Like {@link findBranchKeysContaining} but only keys for the given navbar section. */
export function findBranchKeysContainingInSection(
	queryClient: QueryClient,
	itemKey: string,
	section: NavbarSection,
): readonly (readonly unknown[])[] {
	return findBranchKeysContaining(queryClient, itemKey).filter(
		(key) => key[1] === section,
	);
}

/**
 * Finds an item in any cached navbar branch for the section (root + nested).
 */
export function findNavbarBranchItemInCache(
	queryClient: QueryClient,
	section: NavbarSection,
	key: string,
): NavbarBranchItem | undefined {
	const queries = queryClient.getQueriesData<NavbarBranchItem[]>({
		queryKey: navbarBranchQueryKeys.section(section),
	});
	for (const [, data] of queries) {
		if (!data) continue;
		const found = data.find((i) => i.key === key);
		if (found) return found;
	}
	return undefined;
}
